<template>
  <div class="wrapper">
    <div class="tw-container tw-mx-auto tw-transition-all lg:tw-px-32 md:tw-px-16">

      <template v-if="state.step == 1">
        <h1 class="tw-text-6xl tw-mb-4 tw-font-bold tw-text-center">Start a New Order</h1>
        <h3 class="tw-text-2xl tw-mb-4 tw-font-bold tw-text-center">- OR -</h3>
        <h4 class="tw-text-2xl tw-mb-4 tw-font-bold tw-text-center"><span class="hover:tw-underline tw-text-orange-medium tw-cursor-pointer" @click="scrollToPreviousOrders">View Recent Orders</span></h4>
      </template>

      <StepProgress
        v-if="!state.editMode"
        :step="state.step"
        class="tw-mt-20"
      />

      <div class="tw-bg-white tw-shadow-xl tw-rounded-3xl tw-overflow-hidden tw-min-h-32 tw-mb-12">
        <LocationsView 
          v-if="state.step == 1" 
          :selectedLocation="state.selectedLocation"
          @order-type-set="setOrderType"
          @select-location="setLocation" />

        <EventInfoView 
          :guestCount="state.cateringOrder.guest_count"
          @guest-count-set="setGuestCount"
          :eventType="state.cateringOrder.event_type"
          @event-type-set="setEventType"
          v-if="state.step == 2" />

        <GuestInfoView 
          :catering-order="state.cateringOrder"
          v-if="state.step == 3" />

        <TimeWanted 
          :handoffMode="state.handoffMode"
          :selected-location="state.selectedLocation"
          :guestCount="state.cateringOrder.guest_count"
          :editMode="state.editMode"
          @time-wanted-set="setTimeWanted"
          :time-wanted="state.timeWanted"
          v-if="state.step == 4 && state.selectedLocation" />
      </div>

      <div class="tw-flex tw-flex-wrap tw-flex-row tw-mx-8 lg:tw-mx-auto">
        <div class="tw-w-full lg:tw-w-auto lg:tw-ml-auto lg:tw-order-3">
          <TwButton 
            cssClass="tw-mr-4 tw-w-full lg:tw-w-auto tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200" 
            @emit-click="skipStep()" 
            v-if="stepIsSkippable"
            :isDisabled="false" 
            buttonText="Skip"/>
          <TwButton 
            cssClass="tw-block tw-w-full lg:tw-w-auto md:tw-inline-block tw-bg-orange-medium hover:tw-bg-orange-dark tw-border-orange-medium tw-border tw-rounded-full tw-text-white tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4  tw-transition-all tw-duration-200" 
            @emit-click="nextStep()" 
            :isLoading="state.submittingCateringOrder"
            :isDisabled="false" 
            :buttonText="!state.editMode ? 'Next' : 'Update'"/>
        </div>

        <TwButton 
          cssClass="lg:tw-order-1 tw-w-1/2 lg:tw-w-auto lg:tw-flex-grow-0 lg:tw-mr-4 tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200"
          @emit-click="prevStep()" 
          v-if="state.step > 1 && (!state.editMode || state.editMode && state.step == 3 )" 
          :isDisabled="false" 
          buttonText="Back"/>

        <TwButton 
          cssClass="lg:tw-order-2 tw-w-1/2 lg:tw-flex-grow-0 lg:tw-w-auto lg:tw-mr-4 tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200" 
          @emit-click="cancelEdit" 
          v-if="canCancel"
          :isDisabled="false" 
          buttonText="Cancel"/>

      </div>

      <div class="tw-mb-36 tw-py-8 tw-mt-2 previous-orders" v-if="state.step == 1">
        <h1 class="tw-text-6xl tw-mb-12 tw-font-bold tw-text-center">Previous Orders</h1>
        <v3Carousel
          v-bind="carouselConfig"
          v-if="(userStore.upcomingOrders != null && userStore.upcomingOrders.length > 0) || (userStore.orders != null && userStore.orders.length > 0)">
          <v3CarouselSlide
            v-for="(order, index) in ((userStore.upcomingOrders || []).concat(userStore.orders || []).sort((a, b) => a.time_wanted < b.time_wanted ? 1 : -1))" 
            :key="'order'+index">
            <PreviousOrder
              :class="['tw-w-full tw-bg-white tw-shadow-xl tw-border tw-rounded-2xl tw-flex-shrink-0 tw-px-8 tw-mx-8 tw-flex tw-flex-col', 
              'tw-py-8 tw-my-16']" 
              :index="index"
              :reordering="addingRecentOrder != null && addingRecentOrder.id == order.id"
              :disabled="addingRecentOrder != null"
              :editingOrder="false"
              :editable="false"
              :currentlyEditing="false"
              @handleRecentOrderModal="popRecentOrderModal(order)"
              @handleRecentOrderClick="popRecentOrderModal"
              @handleEditOrderClick="popRecentOrderModal"
              @handleCancelOrderClick="popRecentOrderModal"
              :carousel="true"
              :locations="locationsStore.locations"
              :order="order" />
          </v3CarouselSlide>
          <template #addons>
            <v3CarouselNav />
          </template>
        </v3Carousel>
        <p v-else class="text-center">Place a new order to see past orders here!</p>
      </div>
    </div>

    <TwModal
      v-show="state.cateringDisabledModalOpen"
      :hasAccept="true"
      :hasHeader="false"
      @emitClose="closeCateringDisabledModal"
      >
      <template v-slot:body>
        <div class="tw-relative tw-pr-24 tw-w-full">
          <h2 class="tw-mx-8 tw-font-black tw-text-5xl tw-mb-6 lg:tw-my-8">Online catering unavailable at {{ state.cateringDisabledModalLocation ? state.cateringDisabledModalLocation.name : 'this location' }}</h2>
        </div>

        <p class="tw-mx-8 tw-mb-8 tw-text-3xl !tw-leading-relaxed">
         {{ state.cateringDisabledMessage }}
        </p>
      </template>

      <template v-slot:footer>
        <div class="tw-flex tw-justify-center tw-items-center tw-px-8 tw-pt-16 tw-pb-8">
          <TwButton 
            cssClass="tw-bg-orange-medium tw-border-2 tw-border-orange-medium tw-text-white hover:tw-bg-black hover:tw-border-black tw-uppercase tw-font-bold tw-px-16 tw-py-3 tw-transition-all tw-duration-200 tw-ml-8 tw-rounded-full tw-inline-block"
            @emit-click="closeCateringDisabledModal" 
            :isLoading="false"
            :isDisabled="false" 
            :buttonText="'Choose a different location'"/>
        </div>
      </template>
    </TwModal> 

    <TwModal
      v-show="modalRecentOrderOpen"
      :hasAccept="true"
      :hasHeader="false"
      :modalSize="'large'"
      @emitClose="closeRecentOrderModal">

      <template v-slot:body>
        <div class="tw-relative tw-pr-24 tw-w-full">
          <h2 class="tw-mx-8 tw-font-black tw-text-5xl tw-mb-6 lg:tw-my-8">Recent Order</h2>
          <button
            type="button"
            class="tw-text-5xl tw-absolute tw-right-2 tw-top-0 hover:tw-text-orange-medium tw-transition-all tw-duration-300"
            @click="closeRecentOrderModal"
          >
            &times;
          </button>
        </div>

        <div v-if="typeof(recentOrderModalItem) != undefined" class="tw-pb-12">
          <PreviousOrderModalContent 
            :order="recentOrderModalItem!"
            :locations="locationsStore.locations"
            :loading="loading"
            :editingOrder="-1"
            @handleReorderRecentOrderClick="reorderRecentOrder"
            @handleCancelOrderClick="cancelRecentOrder"
            @handleEditOrderClick="editRecentOrder"
          />
        </div>
      </template>
    </TwModal>
  </div>
</template>

<script setup lang="ts">
  import {
    defineComponent, reactive, computed, onMounted,
    ref
  } from 'vue';

  import 'vue3-carousel/dist/carousel.css'
  import { Carousel as v3Carousel, Slide as v3CarouselSlide, Navigation as v3CarouselNav } from 'vue3-carousel';
  
  import { useRoute, useRouter } from 'vue-router'
  // import { storeToRefs } from 'pinia'
  import TwButton from '../../components/TwButton.vue'
  import PreviousOrder from '../menu/_PreviousOrder.vue';
  import PreviousOrderModalContent from '../menu/_PreviousOrderModalContent.vue';

  // Data
  import type GuestInfo from '../../types/GuestInfo'
  import type CateringOrder from '../../types/CateringOrder'
  import type FlashMessage from '../../types/FlashMessage'
  import type Location from '../../classes/Location';
  import type OrderDetail from '../../classes/OrderDetails';

  import { useBasketStore } from "../../stores/basket";
  import { useUserStore } from "../../stores/user";
  import { useCateringOrderStore } from "../../stores/catering_order";
  import { useLocationsStore } from "../../stores/locations";
  import { useFlashMessageStore } from '../../stores/flashMessages';

  // components
  import StepProgress from './_StepProgress.vue'
  import LocationsView from './LocationsView.vue'
  import EventInfoView from './EventInfoView.vue'
  import GuestInfoView from './GuestInfoView.vue'
  import TimeWanted from './TimeWantedView.vue'
  import TwModal from '../../components/TwModal.vue';
  import { DateTime } from 'luxon';

  const route = useRoute();
  const router = useRouter();

  const basketStore = useBasketStore();
  const userStore = useUserStore();
  const cateringOrderStore = useCateringOrderStore();
  const locationsStore = useLocationsStore();
  const flashMessageStore = useFlashMessageStore();
  // const store = useUserStore();

  const addingRecentOrder = ref<OrderDetail | null>(null);
  const modalRecentOrderOpen = ref(false);
  const modalRecentOrder = ref<OrderDetail | null>(null);
  const loading = ref(false);

  const state = reactive({
    step: 1,
    eventType: '',
    selectedLocation: undefined as (Location | undefined),
    handoffMode: 'pickup',
    timeWanted: undefined as (Date | undefined),
    timeWantedDirty: false,
    editMode: false,
    submittingCateringOrder: false,
    cateringOrder: {
      id: 0,
      guest_count: 0,
      event_type: "",
      light_eaters: 0,
      regular_eaters: 0,
      hungry_eaters: 0,
      store: 0,
      time_wanted: '',
      delivery_mode: '',
      basket_id: "",
      diets: [],
      allergens: [],
      assistance_level: 0,
      contact_name: "",
      user_id: 0,
      source_order_id: 0,
    } as CateringOrder,
    cateringDisabledModalOpen: false,
    cateringDisabledModalLocation: undefined as (undefined | Location),
    cateringDisabledMessage: "" as (string | undefined),
  })

  onMounted(() => {
    if (cateringOrderStore.cateringOrder) {
      const proxyOrder = JSON.parse(JSON.stringify(cateringOrderStore.cateringOrder));

      if (proxyOrder.time_wanted && proxyOrder.time_wanted != '') {
        let dt = DateTime.fromISO(cateringOrderStore.cateringOrder!.time_wanted);
        state.timeWanted = dt.toJSDate();
      }

      if (proxyOrder.delivery_mode) {
        state.handoffMode = proxyOrder.delivery_mode;
      }

      state.cateringOrder = proxyOrder;

      // Find the matching location
      if (locationsStore.locations) {
        const matchedLocation = locationsStore.locations.find((loc: Location) => {
          return loc.id == cateringOrderStore.cateringOrder!.store
        })

        state.selectedLocation = matchedLocation
      }
    }

    if (userStore.user) {
      if (userStore.orders == null) {
        userStore.fetchRecentOrders(4).catch(() => {
          // Do nothing
        });
      }
    }

    if (route.params.step) {
      state.editMode = true;
      state.step = parseFloat(route.params.step as string);

      // Now clear it from the param cause Alex doesn't like it showing
      let currentUrl = window.location.href;
      let remove_after = currentUrl.indexOf('wizard');
      let updatedUrl = currentUrl.substring(0, remove_after)+'wizard';

      window.history.pushState({}, document.title, updatedUrl);
    } else {
      // Check if the user has a basket or selected location to start from
      if (basketStore.basket) {
        if (locationsStore.locations) {
          const matchedLocation = locationsStore.locations.find((loc: Location) => {
            return loc.id == basketStore.basket!.vendorid
          })

          state.selectedLocation = matchedLocation
        }
      }
    }
  })

  const carouselConfig = {
    itemsToShow: 2,
    wrapAround: false,
    gap: 16,
    snapAlign: 'start',
    breakpointMode: 'carousel',
    breakpoints: {
      1280: {
        itemsToShow: 4,
      },
      1024: {
        itemsToShow: 3,
      },
      768: {
        itemsToShow: 2,
      },
      640: {
        itemsToShow: 2,
      }
    }
  }

  const cancelEdit = (() => {
    if (route.params && route.params.redirect && router.options.history.state.back) {
      router.push(router.options.history.state.back.toString())
    } else {
      router.push('/menu')
    }
  })

  function prevStep() {
    if (state.step > 1) {
      window.scrollTo(0,0);
      state.step -= 1;
      flashMessageStore.dismissErrors();
    }
  }

  function skipStep() {
    // We clear out the step data if the user opts to skip, unless we are in editMode, then we leave it alone
    if (state.step == 2) {
      if (!state.editMode) {
        state.cateringOrder.guest_count = 0;
        state.cateringOrder.event_type = '';
      }
      state.step += 1;
    } else if (state.step == 3) {
      state.cateringOrder.light_eaters = 0;
      if (state.cateringOrder && state.cateringOrder.guest_count > 0) {
        // Default to the total number of guests if they skip this step
        state.cateringOrder.regular_eaters = state.cateringOrder.guest_count;
      } else {
        state.cateringOrder.regular_eaters = 0;
      }
      state.cateringOrder.hungry_eaters = 0;

      // leave allergens as-is in editMode
      if (!state.editMode) {
        state.cateringOrder.allergens = [];
        state.cateringOrder.diets = [];
      }

      if (state.editMode) {
        router.push('/menu')
      } else {
        state.step += 1;
      }
    }
  }

  function nextStep() {
    const stepValid = validateStep();
    let skipGuestInformation = true;

    if (stepValid) {
      flashMessageStore.dismissErrors();
      if (state.editMode) {
        if (parseFloat(route.params.step as string) == 2 && state.step == 2) {
          window.scrollTo(0,0);
          state.step += 1;
        } else {
          // User navigated to the wizard to edit only a single step
          submitCateringOrder().then(() => {
            if (route.params && route.params.redirect && router.options.history.state.back) {
              router.push(router.options.history.state.back.toString())
            } else {
              router.push('/menu')
            }
          })
        }
      } else {
        if (state.step < 4 && validateStep()) {
          window.scrollTo(0,0);
          state.step += 1;
        } else {
          submitCateringOrder().then(() => {
            router.push('/menu')
          })
        }
      }
    }
  }

  const submitCateringOrder = ((): Promise<any> => {
    state.submittingCateringOrder = true;
    return new Promise((resolve, reject) => {
      if (state.cateringOrder.guest_count == null || typeof(state.cateringOrder.guest_count) == 'undefined' || state.cateringOrder.guest_count == 0) {
      // We clear out the guest hunger levels
      state.cateringOrder.light_eaters = 0;
      state.cateringOrder.regular_eaters = 0;
      state.cateringOrder.hungry_eaters = 0;
      state.cateringOrder.guest_count = 0;
    }

      let order = {
        guest_count: state.cateringOrder.guest_count,
        event_type: state.cateringOrder.event_type,
        light_eaters: state.cateringOrder.light_eaters,
        regular_eaters: state.cateringOrder.regular_eaters,
        hungry_eaters: state.cateringOrder.hungry_eaters,
        store: state.selectedLocation!.id!,
        time_wanted: state.timeWanted!,
        delivery_mode: state.handoffMode,
        basket_id: basketStore.basket?.id,
        allergens: state.cateringOrder.allergens,
        diets: state.cateringOrder.diets,
        assistance_level: cateringOrderStore.assistanceLevel,
      } as unknown as CateringOrder;

      if (basketStore.basket == null || basketStore.basket != null && basketStore.basket!.vendorid != state.selectedLocation!.id) {
        order.basket_id = "";
      }

      userStore.setUserLocation(state.selectedLocation!);

      // If the user has a basket with a different time wanted or no timewanted set; we will have to update it if the timewanted changed
      if (timeWantedNeedsUpdated()) {
        let tw = DateTime.fromJSDate(state.timeWanted!);
        basketStore.setTimeWanted(tw, false).then(() => {
          if (state.cateringOrder.id) {
            cateringOrderStore.updateCateringOrder(state.cateringOrder.id, order).then(() => {
              state.submittingCateringOrder = false;
              resolve('');
            }).catch((error: Error) => {
              state.submittingCateringOrder = false;
              errorFlash('Error', error.message)
            })
          } else {
            cateringOrderStore.createCateringOrder(order).then(() => {
              state.submittingCateringOrder = false;
              resolve('');
            }).catch((error: Error) => {
              state.submittingCateringOrder = false;
              errorFlash('Error', error.message)
            })
          }
        }).catch((error: Error) => {
          state.submittingCateringOrder = false;
           errorFlash('Error', error.message)
        })
      } else {
        if (state.cateringOrder.id) {
          cateringOrderStore.updateCateringOrder(state.cateringOrder.id, order).then(() => {
            state.submittingCateringOrder = false;
            resolve('');
          }).catch((error: Error) => {
            state.submittingCateringOrder = false;
            errorFlash('Error', error.message)
          })
        } else {
          cateringOrderStore.createCateringOrder(order).then(() => {
            state.submittingCateringOrder = false;
            resolve('');
          }).catch((error: Error) => {
            state.submittingCateringOrder = false;
            errorFlash('Error', error.message)
          })
        }
      }
    })
  })

  const canCancel = computed(():boolean => {
    let cancellable = false;

    if (state.step == 4) {
      // Make sure the user has a time set
      cancellable = cateringOrderStore.cateringOrder != null && cateringOrderStore.cateringOrder?.time_wanted != null;
    } else {
      cancellable = state.editMode;
    }

    return cancellable;
  })

  const timeWantedNeedsUpdated = (():boolean => {
    let bool = false;
    if (basketStore.basket) {
      if (basketStore.basket.timewanted && state.timeWantedDirty == true) {
        const requestedTW = DateTime.fromJSDate(state.timeWanted!);
        const basketTW =  DateTime.fromFormat(basketStore.basket.timewanted, 'yyyyMMdd hh:mm');

        bool = (requestedTW != basketTW);
      }
    }

    return bool;
  })

  const stepIsSkippable = computed(() => {
    return !state.editMode && (state.step == 2 || state.step == 3);
  })

  function validateStep(): boolean {
    let valid = false;
    if (state.selectedLocation != null && typeof(state.selectedLocation.id) != "undefined" && state.selectedLocation?.catering_disabled) {
      state.cateringDisabledModalOpen = true;
      state.cateringDisabledMessage = state.selectedLocation.catering_disabled_message;
      state.cateringDisabledModalLocation = state.selectedLocation;
      return false;
    }
    if (state.step == 1) {
      const locationValid = (state.selectedLocation != null && typeof(state.selectedLocation.id) != undefined);
      const handoffModeValid = (state.handoffMode != null);
      valid = locationValid && handoffModeValid;

      if (!valid) {
        errorFlash('Error', 'Please select a location for your order.')
      }
    } else if (state.step == 2) {
      valid = true;
      // const guestCountValid = state.cateringOrder.guest_count != null && state.cateringOrder.guest_count > 0;
      // const eventTypeValid = state.cateringOrder.event_type != null && state.cateringOrder.event_type != '';
      // valid = guestCountValid && eventTypeValid;

      // if (!valid) {
      //   errorFlash('Error', 'Please set the guest count and event type for your order or skip this step.')
      // }
    } else if (state.step == 3) {
      let totalFeeds = (state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters);
      if ((state.cateringOrder && state.cateringOrder.guest_count == 0 ) || totalFeeds == state.cateringOrder.guest_count) {
        valid = true;
      } else {
        valid = false;
        if (totalFeeds < state.cateringOrder.guest_count) {
          errorFlash('Error', 'You have selected less guests to feed than your provided number of anticipated guests in the previous step. Please update the number of anticipated guests in the previous step or update your current counts above to match.')
        } else {
        errorFlash('Error', 'You have selected more guests to feed than your provided number of anticipated guests in the previous step. Please update the number of anticipated guests in the previous step or update your current counts above to match.')
        }
      } 

    }
    else if (state.step == 4) {
      const timeWantedValid = (state.timeWanted != null && state.timeWanted > new Date);
      valid = timeWantedValid;

      if (!valid) {
        errorFlash('Error', 'Please set a valid time for your order.')
      }
    }
    
    return valid;
  }

  const setGuestCount = (amount: number) => {
    state.cateringOrder.guest_count = amount
  }

  const setEventType = (eventType: string) => {
    state.cateringOrder.event_type = eventType
  }

  const setLocation = (location: Location) => {
    state.selectedLocation = location

    if (state.selectedLocation.catering_disabled) {
      state.cateringDisabledModalOpen = true;
      state.cateringDisabledMessage = location.catering_disabled_message;
      state.cateringDisabledModalLocation = location;
    } else {
      nextStep();
    }
  }

  const closeCateringDisabledModal = () => {
    state.cateringDisabledModalOpen = false;
    window.setTimeout(function() {
      state.cateringDisabledMessage = undefined;
      state.cateringDisabledModalLocation = undefined;
    }, 500);
  }

  const setOrderType = (mode: string) => {
    state.handoffMode = mode
  }

  const setTimeWanted = (time: Date) => {
    state.timeWanted = time
    state.timeWantedDirty = true;
  }

  const errorFlash = (title: string, text: string) => {
    const message = {
      id: Math.round(Date.now() / 1000),
      messageType: "danger",
      text: text,
      headline: title,
      delay: 0, // ms
      icon: "",
      cssClass: "",
    } as FlashMessage;
    flashMessageStore.addMessage(message);
  }
  
  const closeRecentOrderModal = () => {
    modalRecentOrderOpen.value = false;
    window.setTimeout(function() {
      modalRecentOrder.value = null;
    }, 500);
  }

  const popRecentOrderModal = (order: OrderDetail) => {
    window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    if (order) {
      modalRecentOrder.value = order;
      modalRecentOrderOpen.value = true;
    }
  }

  const scrollToPreviousOrders = () => {
    const previousOrderHeight = (document.querySelector('.previous-orders') as HTMLElement)?.offsetTop || 0;
            
    window.scrollTo({left: 0, top: previousOrderHeight - 100, behavior: "smooth"});
  }

  const recentOrderModalItem = computed(() => { 
    if (modalRecentOrder.value != null && modalRecentOrder.value != undefined) {
      return (modalRecentOrder.value);
    } else {
      return null;
    }
  })

  const reorderRecentOrder = ((order: OrderDetail) => {
    addingRecentOrder.value = order;
    loading.value = true;
    if (order.id) {
      basketStore.reorder(order.id).then(() => {
        addingRecentOrder.value = null;
        loading.value = false;
        closeRecentOrderModal();
        if (cateringOrderStore.cateringOrder != null && cateringOrderStore.cateringOrder.time_wanted != null) {
          const message = {
            id: Math.round(Math.random() * 10000000),
            messageType: "success",
            text: "Your order is ready to place! Head to checkout or make any changes you need first",
            headline: "",
            delay: 5000, // ms
            icon: "",
            cssClass: "",
          } as FlashMessage;
          flashMessageStore.addMessage(message);
          router.push('/menu');
        } else {
          const message = {
            id: Math.round(Math.random() * 10000000),
            messageType: "success",
            text: "Please select a time before continuing with your order",
            headline: "Re-Order Time",
            delay: 5000, // ms
            icon: "",
            cssClass: "",
          } as FlashMessage;
          flashMessageStore.addMessage(message);
          // Force user to set time wanted
          router.push('/wizard/4');
        }
      }).catch((error) => {
        closeRecentOrderModal();
        if (error.response && error.response.data && error.response.data.message) {
          const message = {
              id: Math.round(Math.random() * 10000000),
              messageType: "danger",
              text: error.response.data.message,
              headline: "Unable to Re-Order",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);
        }
        addingRecentOrder.value = null;
        loading.value = false;
      });
    } else {
      closeRecentOrderModal();
      addingRecentOrder.value = null;
      loading.value = false;
    }
  })

  const editRecentOrder = ((order: OrderDetail) => {
    loading.value = true;
    basketStore.editOrder(order.id).then(() => {
      loading.value = false;
      closeRecentOrderModal();
      const message = {
        id: Math.round(Math.random() * 10000000),
        messageType: "success",
        text: "You are now editing an upcoming order. You must place this new order or the original order will still be prepared and charged.",
        headline: "",
        delay: 5000, // ms
        icon: "",
        cssClass: "",
      } as FlashMessage;
      basketStore.editing = true; // we know we're editing, go ahead and pre-set this versus waiting for it to load
      basketStore.hasAcknowledgedEditing = false;
      flashMessageStore.addMessage(message);
      router.push('/menu');
    }).catch((error) => {
      loading.value = false;
      closeRecentOrderModal();
      if (error.response && error.response.data && error.response.data.message) {
        const message = {
            id: Math.round(Math.random() * 10000000),
            messageType: "danger",
            text: error.response.data.message,
            headline: "Unable to Edit Order",
            delay: 5000, // ms
            icon: "",
            cssClass: "",
          } as FlashMessage;
          flashMessageStore.addMessage(message);
      }
    });
  })

  const cancelRecentOrder =((order: OrderDetail) => {
    loading.value = true;
    basketStore.cancelOrder(order.id).then(() => {
      loading.value = false;
      closeRecentOrderModal();
      const message = {
        id: Math.round(Date.now() / 1000),
        messageType: "success",
        text: "Your order has been canceled.",
        headline: "Order Updated",
        delay: 0, // ms
        icon: "",
        cssClass: "",
      } as FlashMessage;
      flashMessageStore.addMessage(message);
    }).catch((error) => {
      loading.value = false;
      closeRecentOrderModal();
      if (error.message) {
        const message = {
            id: Math.round(Math.random() * 10000000),
            messageType: "danger",
            text: error.message,
            headline: "Unable to Cancel Order",
            delay: 5000, // ms
            icon: "",
            cssClass: "",
          } as FlashMessage;
          flashMessageStore.addMessage(message);
      }
    });
  })

</script>